import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  Wand2, Brain, Landmark, Coins, ArrowRight, Network, BookMarked, Target, LogOut, 
  ChevronLeft, ChevronRight, Quote, Check, Circle, Hexagon, Octagon, 
  Zap, PenLine, GitBranch, GraduationCap, Briefcase, FlaskConical,
  ChevronDown, Search, MessageSquare, Award
} from 'lucide-react';
import api from '@/lib/api';
import { toast } from '@/components/ui/sonner';
import useUiStore from '@/store/uiStore';
import Logo from '@/components/core/Logo';

export default function LandingPage() {
  const { user, token, logout } = useAuth();
  const navigate = useNavigate();
  const { openUpgradeModal } = useUiStore();
  const [prompt, setPrompt] = useState('');
  const [persona, setPersona] = useState('student');
  const [generating, setGenerating] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);

  const handleGenerate = async (topicOverride) => {
    const text = topicOverride || prompt;
    if (!text.trim()) { toast.error('Enter a topic to explore'); return; }
    if (!token) { navigate('/login?redirect=/'); return; }
    setGenerating(true);
    try {
      const res = await api.post('/ai/generate', { prompt: text, persona });
      sessionStorage.setItem('atlas_generated_map', JSON.stringify(res.data));
      navigate('/canvas?generated=true');
    } catch (err) {
      if (err.response?.status === 403) {
        openUpgradeModal();
      } else {
        toast.error('Failed to generate map. Try again.');
      }
    } finally {
      setGenerating(false);
    }
  };

  const quickTopics = [
    { label: 'Ancient Rome', icon: Landmark },
    { label: 'The Human Brain', icon: Brain },
    { label: 'Personal Finance', icon: Coins },
  ];

  const testimonials = [
    { name: 'Sarah Chen', role: 'Medical Student', content: 'Atlas transformed how I study anatomy. The visual mind maps help me connect complex systems.', avatar: 'SC' },
    { name: 'Marcus Johnson', role: 'Software Engineer', content: 'I use Atlas to map out new programming concepts. The AI explanations break down complex topics.', avatar: 'MJ' },
    { name: 'Elena Rodriguez', role: 'History Teacher', content: 'My students love exploring historical events through mind maps. The mastery tracking keeps them engaged.', avatar: 'ER' },
    { name: 'David Park', role: 'Product Manager', content: 'Perfect for strategic planning and knowledge organization. I map out product roadmaps effortlessly.', avatar: 'DP' },
    { name: 'Aisha Patel', role: 'PhD Researcher', content: 'The ability to upload PDFs and generate structured maps from research papers is incredible.', avatar: 'AP' }
  ];

  const [currentTestimonial, setCurrentTestimonial] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [testimonials.length]);

  const faqs = [
    { q: 'What makes Atlas different from other mind mapping tools?', a: 'Atlas uses AI to automatically generate comprehensive mind maps from any topic. It also provides AI-powered explanations, quizzes, and mastery tracking for each node - turning passive maps into active learning tools.' },
    { q: 'How do AI credits work?', a: 'AI credits are used for AI-powered features: generating maps (1 credit), getting explanations (1 credit), asking questions (1 credit), and taking quizzes (1 credit). Free users get 10 credits to start.' },
    { q: 'Can I create maps without AI?', a: 'Absolutely! You can open a blank canvas and manually create nodes, connect them, and build your own structure. AI is optional - use it when you need a head start or deeper insights.' },
    { q: 'What file formats can I upload?', a: 'You can upload PDF and TXT files as reference material. Atlas will use the content to generate more relevant and detailed mind maps tailored to your source.' },
  ];

  return (
    <div className="min-h-screen bg-background" data-testid="landing-page">
      {/* Navbar */}
      <nav className="sticky top-0 z-50 border-b border-border bg-background/80 backdrop-blur-sm" data-testid="landing-navbar">
        <div className="max-w-6xl mx-auto px-6 h-14 flex items-center justify-between">
          <Logo data-testid="logo-link" />
          <div className="flex items-center gap-3">
            {token ? (
              <>
                <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')} data-testid="nav-dashboard-btn" className="font-body text-sm">
                  Dashboard
                </Button>
                <Button variant="ghost" size="sm" onClick={logout} data-testid="nav-logout-btn" className="font-body text-sm">
                  <LogOut className="w-4 h-4 mr-1" strokeWidth={1.5} /> Sign out
                </Button>
              </>
            ) : (
              <>
                <Button variant="ghost" size="sm" onClick={() => navigate('/login')} data-testid="nav-login-btn" className="font-body text-sm">
                  Log in
                </Button>
                <Button size="sm" onClick={() => navigate('/register')} data-testid="nav-register-btn"
                  className="bg-cobalt text-white hover:bg-cobalt/90 font-body text-sm btn-press">
                  Get Started
                </Button>
              </>
            )}
          </div>
        </div>
      </nav>

      {/* Hero Section - Asymmetric Split */}
      <section className="max-w-6xl mx-auto px-6 pt-16 pb-20">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          {/* Left - Typography Heavy */}
          <div className="lg:col-span-7 animate-fade-in-up">
            <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-[300] tracking-tight leading-[1.05] text-foreground mb-6">
              Turn any topic into<br />
              <span className="font-[600] relative">
                structured knowledge
                <svg className="absolute -bottom-2 left-0 w-full" height="8" viewBox="0 0 200 8" fill="none">
                  <path d="M1 5.5C47 2 153 2 199 5.5" stroke="#FF4F00" strokeWidth="3" strokeLinecap="round"/>
                </svg>
              </span>
            </h1>
            <p className="font-body text-lg leading-relaxed text-muted-foreground max-w-lg mb-8">
              Atlas generates interactive mind maps from any subject. Explore concepts visually, 
              get AI explanations, take quizzes, and track your mastery.
            </p>
            <div className="flex flex-wrap items-center gap-4">
              <Button size="lg" onClick={() => document.getElementById('composer')?.scrollIntoView({ behavior: 'smooth' })}
                data-testid="hero-cta-btn"
                className="bg-orange text-white hover:bg-orange/90 px-8 h-12 text-base font-body font-medium btn-press hover:shadow-[3px_3px_0px_0px_rgba(0,0,0,1)] transition-all">
                Try It Free <ArrowRight className="w-4 h-4 ml-2" strokeWidth={2} />
              </Button>
              <Button size="lg" variant="outline" onClick={() => navigate(token ? '/canvas' : '/register')}
                className="px-8 h-12 text-base font-body border-foreground/20 hover:bg-muted">
                Open Blank Canvas
              </Button>
            </div>
          </div>
          
          {/* Right - Real Mind Map Preview */}
          <div className="lg:col-span-5 animate-fade-in-up" style={{ animationDelay: '150ms' }}>
            <div className="relative">
              {/* Decorative elements */}
              <div className="absolute -top-4 -right-4 w-24 h-24 border border-cobalt/20 rounded-md -z-10" />
              <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-orange/5 rounded-md -z-10" />
              
              {/* Mind Map Image Card */}
              <div className="border-2 border-foreground bg-card rounded-md overflow-hidden shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
                <div className="flex items-center gap-2 px-4 py-2 border-b border-border bg-muted/30">
                  <div className="w-2.5 h-2.5 rounded-full bg-red-400" />
                  <div className="w-2.5 h-2.5 rounded-full bg-yellow-400" />
                  <div className="w-2.5 h-2.5 rounded-full bg-green-400" />
                  <span className="ml-2 font-mono text-[10px] text-muted-foreground">Machine Learning</span>
                </div>
                <img 
                  src="https://customer-assets.emergentagent.com/job_208c0375-d268-49f3-a201-b4a2d0704403/artifacts/zlhm1ci6_image.png" 
                  alt="Mind map example showing Machine Learning concepts with branches for Supervised Learning, Deep Learning, and more"
                  className="w-full h-auto"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Composer Section */}
      <section id="composer" className="max-w-6xl mx-auto px-6 pb-24">
        <div className="max-w-2xl mx-auto">
          <div className="border-2 border-foreground bg-card rounded-md overflow-hidden shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] animate-fade-in-up" data-testid="composer-card">
            <div className="flex items-center gap-3 px-5 py-3 border-b border-border bg-muted/30">
              <Select value={persona} onValueChange={setPersona} data-testid="persona-select">
                <SelectTrigger className="w-[140px] h-8 text-sm border-border bg-card" data-testid="persona-select-trigger">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="student" data-testid="persona-student">Student</SelectItem>
                  <SelectItem value="researcher" data-testid="persona-researcher">Researcher</SelectItem>
                  <SelectItem value="professional" data-testid="persona-professional">Professional</SelectItem>
                </SelectContent>
              </Select>
              <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Persona</span>
            </div>
            <textarea
              value={prompt}
              onChange={e => setPrompt(e.target.value)}
              placeholder="What would you like to explore? Try 'Machine Learning fundamentals' or 'World War II causes'..."
              className="w-full px-5 py-4 text-base font-body bg-transparent border-0 outline-none resize-none placeholder:text-muted-foreground/50"
              rows={3}
              data-testid="prompt-textarea"
              onKeyDown={e => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) handleGenerate(); }}
            />
            <div className="flex items-center justify-between px-5 py-3 border-t border-border bg-muted/20">
              <div className="flex items-center gap-2">
                <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground mr-1">Quick:</span>
                {quickTopics.map(topic => (
                  <button
                    key={topic.label}
                    onClick={() => { setPrompt(topic.label); handleGenerate(topic.label); }}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-body rounded-md border border-border bg-card hover:border-foreground/30 hover:shadow-[2px_2px_0px_0px_rgba(0,0,0,0.5)] transition-all"
                    data-testid={`quick-topic-${topic.label.toLowerCase().replace(/\s/g, '-')}`}
                  >
                    <topic.icon className="w-3 h-3" strokeWidth={1.5} />
                    {topic.label}
                  </button>
                ))}
              </div>
              <Button
                onClick={() => handleGenerate()}
                disabled={generating || !prompt.trim()}
                data-testid="generate-btn"
                className="bg-cobalt text-white hover:bg-cobalt/90 font-body text-sm btn-press h-9 px-5"
              >
                {generating ? (
                  <span className="flex items-center gap-2"><span className="spinner !w-4 !h-4 !border-white/30 !border-t-white" /> Generating...</span>
                ) : (
                  <span className="flex items-center gap-1"><Wand2 className="w-3.5 h-3.5" strokeWidth={1.5} /> Generate</span>
                )}
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works - Horizontal Steps */}
      <section className="border-y border-border bg-muted/20 py-20" data-testid="how-it-works-section">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex items-end justify-between mb-12">
            <div>
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground block mb-2">Process</span>
              <h2 className="font-heading text-3xl md:text-4xl font-[400] tracking-tight">How Atlas Works</h2>
            </div>
            <span className="font-mono text-[10px] text-muted-foreground hidden md:block">3 simple steps</span>
          </div>
          
          <div className="grid md:grid-cols-3 gap-0">
            {[
              { step: '01', icon: PenLine, title: 'Enter Your Topic', desc: 'Type any subject or upload a PDF/document as reference material for your mind map.' },
              { step: '02', icon: GitBranch, title: 'AI Generates Map', desc: 'Our AI creates a comprehensive, hierarchical mind map with branches and descriptions.' },
              { step: '03', icon: Search, title: 'Explore & Learn', desc: 'Click nodes for AI explanations, ask questions, take quizzes, and track your mastery.' },
            ].map((item, i) => (
              <div key={item.step} className={`relative p-8 ${i !== 2 ? 'md:border-r border-border' : ''}`}>
                <div className="flex items-start gap-4">
                  <span className="font-mono text-4xl font-[200] text-border">{item.step}</span>
                  <div>
                    <div className="w-10 h-10 rounded-md bg-foreground flex items-center justify-center mb-4">
                      <item.icon className="w-5 h-5 text-background" strokeWidth={1.5} />
                    </div>
                    <h3 className="font-heading text-lg font-[500] mb-2">{item.title}</h3>
                    <p className="font-body text-sm text-muted-foreground leading-relaxed">{item.desc}</p>
                  </div>
                </div>
                {i !== 2 && (
                  <ArrowRight className="hidden md:block absolute right-0 top-1/2 translate-x-1/2 -translate-y-1/2 w-5 h-5 text-border" strokeWidth={1.5} />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases - Bento Grid */}
      <section className="max-w-6xl mx-auto px-6 py-24" data-testid="use-cases-section">
        <div className="mb-12">
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground block mb-2">Use Cases</span>
          <h2 className="font-heading text-3xl md:text-4xl font-[400] tracking-tight">Built for every learner</h2>
        </div>
        
        <div className="grid md:grid-cols-12 gap-4">
          {/* Large card - Students */}
          <div className="md:col-span-7 border border-border bg-card rounded-md p-8 card-hover group" data-testid="usecase-students">
            <div className="flex items-start justify-between mb-6">
              <div className="w-12 h-12 rounded-md bg-cobalt/10 flex items-center justify-center group-hover:bg-cobalt/20 transition-colors">
                <GraduationCap className="w-6 h-6 text-cobalt" strokeWidth={1.5} />
              </div>
              <span className="font-mono text-[10px] text-muted-foreground uppercase tracking-wider">Most Popular</span>
            </div>
            <h3 className="font-heading text-2xl font-[500] mb-3">Students & Self-Learners</h3>
            <p className="font-body text-muted-foreground leading-relaxed mb-6">
              Transform textbooks, lectures, and study materials into visual mind maps. 
              Use AI quizzes to test yourself and track your understanding across topics.
            </p>
            <div className="flex flex-wrap gap-2">
              {['Exam Prep', 'Note Taking', 'Research', 'Languages'].map(tag => (
                <span key={tag} className="px-3 py-1 bg-muted rounded-full font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
                  {tag}
                </span>
              ))}
            </div>
          </div>
          
          {/* Right column - stacked */}
          <div className="md:col-span-5 flex flex-col gap-4">
            <div className="border border-border bg-card rounded-md p-6 card-hover group" data-testid="usecase-professionals">
              <div className="w-10 h-10 rounded-md bg-orange/10 flex items-center justify-center mb-4 group-hover:bg-orange/20 transition-colors">
                <Briefcase className="w-5 h-5 text-orange" strokeWidth={1.5} />
              </div>
              <h3 className="font-heading text-lg font-[500] mb-2">Professionals</h3>
              <p className="font-body text-sm text-muted-foreground leading-relaxed">
                Map out business strategies, project plans, and competitive landscapes. Onboard faster to new domains.
              </p>
            </div>
            
            <div className="border border-border bg-card rounded-md p-6 card-hover group" data-testid="usecase-researchers">
              <div className="w-10 h-10 rounded-md bg-green-500/10 flex items-center justify-center mb-4 group-hover:bg-green-500/20 transition-colors">
                <FlaskConical className="w-5 h-5 text-green-600" strokeWidth={1.5} />
              </div>
              <h3 className="font-heading text-lg font-[500] mb-2">Researchers</h3>
              <p className="font-body text-sm text-muted-foreground leading-relaxed">
                Upload papers, generate literature maps, and connect concepts across multiple sources.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="max-w-6xl mx-auto px-6 py-24" data-testid="features-section">
        <div className="mb-12">
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground block mb-2">Features</span>
          <h2 className="font-heading text-3xl md:text-4xl font-[400] tracking-tight">Everything you need to learn</h2>
        </div>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 stagger-children">
          {[
            { icon: Network, title: 'Visual Maps', desc: 'AI generates radial mind maps with color-coded branches.' },
            { icon: MessageSquare, title: 'AI Chat', desc: 'Ask questions about any node and get instant answers.' },
            { icon: BookMarked, title: 'Deep Explanations', desc: 'Get detailed summaries and deep-dives on demand.' },
            { icon: Award, title: 'Mastery Tracking', desc: 'Track progress from learning to mastered per node.' },
          ].map(f => (
            <div key={f.title} className="border border-border bg-card p-6 rounded-md card-hover" data-testid={`feature-${f.title.toLowerCase().replace(/\s/g, '-')}`}>
              <f.icon className="w-5 h-5 text-cobalt mb-4" strokeWidth={1.5} />
              <h3 className="font-heading text-base font-[500] mb-2">{f.title}</h3>
              <p className="font-body text-sm text-muted-foreground leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Pricing Section */}
      <section className="max-w-6xl mx-auto px-6 pb-24" data-testid="pricing-section">
        <div className="text-center mb-12">
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-4 block">Pricing</span>
          <h2 className="font-heading text-3xl md:text-4xl font-[400] tracking-tight mb-3">Simple, transparent pricing</h2>
          <p className="font-body text-muted-foreground max-w-lg mx-auto">Start free. Upgrade when you need more power.</p>
        </div>

        <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
          {/* Free */}
          <div className="border border-border bg-card rounded-md p-6 flex flex-col" data-testid="pricing-free">
            <div className="mb-6">
              <Circle className="w-5 h-5 text-muted-foreground mb-3" strokeWidth={1.5} />
              <h3 className="font-heading text-xl font-[500]">Free</h3>
              <div className="flex items-baseline gap-1 mt-2">
                <span className="font-heading text-4xl font-[600]">$0</span>
              </div>
            </div>
            <ul className="space-y-2 mb-6 flex-1">
              {['10 AI credits', '1 AI-generated map', '3 total maps', 'AI explanations'].map(f => (
                <li key={f} className="flex items-center gap-2 font-body text-sm">
                  <Check className="w-4 h-4 text-green-500" strokeWidth={2} />{f}
                </li>
              ))}
            </ul>
            <Button variant="outline" onClick={() => navigate('/register')} className="w-full font-body">Get Started</Button>
          </div>

          {/* Pro */}
          <div className="border-2 border-cobalt bg-card rounded-md p-6 flex flex-col relative shadow-[4px_4px_0px_0px_rgba(0,71,171,0.3)]" data-testid="pricing-pro">
            <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-cobalt text-white px-3 py-1 rounded-full">
              <span className="font-mono text-[10px] uppercase tracking-wider">Popular</span>
            </div>
            <div className="mb-6">
              <Hexagon className="w-5 h-5 text-cobalt mb-3" strokeWidth={1.5} />
              <h3 className="font-heading text-xl font-[500]">Pro</h3>
              <div className="flex items-baseline gap-1 mt-2">
                <span className="font-heading text-4xl font-[600]">$15</span>
                <span className="font-body text-sm text-muted-foreground">/mo</span>
              </div>
            </div>
            <ul className="space-y-2 mb-6 flex-1">
              {['50 AI credits/mo', 'Unlimited maps', 'Export PNG & PDF', 'Priority support'].map(f => (
                <li key={f} className="flex items-center gap-2 font-body text-sm">
                  <Check className="w-4 h-4 text-cobalt" strokeWidth={2} />{f}
                </li>
              ))}
            </ul>
            <Button onClick={() => token ? openUpgradeModal() : navigate('/register')} className="w-full font-body bg-cobalt hover:bg-cobalt/90 text-white">
              Upgrade to Pro
            </Button>
          </div>

          {/* Max */}
          <div className="border border-border bg-card rounded-md p-6 flex flex-col" data-testid="pricing-max">
            <div className="mb-6">
              <Octagon className="w-5 h-5 text-orange mb-3" strokeWidth={1.5} />
              <h3 className="font-heading text-xl font-[500]">Max</h3>
              <div className="flex items-baseline gap-1 mt-2">
                <span className="font-heading text-4xl font-[600]">$29</span>
                <span className="font-body text-sm text-muted-foreground">/mo</span>
              </div>
            </div>
            <ul className="space-y-2 mb-6 flex-1">
              {['150 AI credits/mo', 'Unlimited depth', 'Export PNG & PDF', 'Priority support'].map(f => (
                <li key={f} className="flex items-center gap-2 font-body text-sm">
                  <Check className="w-4 h-4 text-orange" strokeWidth={2} />{f}
                </li>
              ))}
            </ul>
            <Button variant="outline" onClick={() => token ? openUpgradeModal() : navigate('/register')} 
              className="w-full font-body border-orange text-orange hover:bg-orange hover:text-white">
              Upgrade to Max
            </Button>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="border-y border-border bg-muted/20 py-20" data-testid="testimonials-section">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex items-end justify-between mb-12">
            <div>
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground block mb-2">Testimonials</span>
              <h2 className="font-heading text-3xl md:text-4xl font-[400] tracking-tight">Loved by learners</h2>
            </div>
            <div className="flex gap-2">
              <button onClick={() => setCurrentTestimonial(p => (p - 1 + testimonials.length) % testimonials.length)}
                className="w-10 h-10 rounded-md border border-border bg-card hover:bg-muted flex items-center justify-center transition-colors"
                data-testid="testimonial-prev">
                <ChevronLeft className="w-5 h-5" strokeWidth={1.5} />
              </button>
              <button onClick={() => setCurrentTestimonial(p => (p + 1) % testimonials.length)}
                className="w-10 h-10 rounded-md border border-border bg-card hover:bg-muted flex items-center justify-center transition-colors"
                data-testid="testimonial-next">
                <ChevronRight className="w-5 h-5" strokeWidth={1.5} />
              </button>
            </div>
          </div>
          
          <div className="relative overflow-hidden">
            <div className="flex transition-transform duration-500 ease-out" style={{ transform: `translateX(-${currentTestimonial * 100}%)` }}>
              {testimonials.map((t, i) => (
                <div key={i} className="w-full flex-shrink-0 px-4" data-testid={`testimonial-${i}`}>
                  <div className="max-w-2xl mx-auto">
                    <Quote className="w-10 h-10 text-cobalt/20 mb-4" strokeWidth={1.5} />
                    <p className="font-body text-xl md:text-2xl leading-relaxed text-foreground mb-6">"{t.content}"</p>
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-full bg-cobalt/10 flex items-center justify-center">
                        <span className="font-mono text-sm font-medium text-cobalt">{t.avatar}</span>
                      </div>
                      <div>
                        <p className="font-body font-medium text-foreground">{t.name}</p>
                        <p className="font-body text-sm text-muted-foreground">{t.role}</p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          <div className="flex justify-center gap-2 mt-8">
            {testimonials.map((_, i) => (
              <button key={i} onClick={() => setCurrentTestimonial(i)}
                className={`w-2 h-2 rounded-full transition-colors ${i === currentTestimonial ? 'bg-cobalt' : 'bg-border'}`}
                data-testid={`testimonial-dot-${i}`} />
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="max-w-6xl mx-auto px-6 py-24" data-testid="faq-section">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-12">
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground block mb-2">FAQ</span>
            <h2 className="font-heading text-3xl font-[400] tracking-tight">Common questions</h2>
          </div>
          
          <div className="space-y-3">
            {faqs.map((faq, i) => (
              <div key={i} className="border border-border rounded-md overflow-hidden" data-testid={`faq-${i}`}>
                <button 
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between p-4 text-left bg-card hover:bg-muted/30 transition-colors"
                >
                  <span className="font-body font-medium pr-4">{faq.q}</span>
                  <ChevronDown className={`w-5 h-5 text-muted-foreground flex-shrink-0 transition-transform ${openFaq === i ? 'rotate-180' : ''}`} strokeWidth={1.5} />
                </button>
                {openFaq === i && (
                  <div className="px-4 pb-4 bg-card">
                    <p className="font-body text-sm text-muted-foreground leading-relaxed">{faq.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="max-w-6xl mx-auto px-6 pb-24">
        <div className="border-2 border-foreground bg-foreground text-background rounded-md p-12 text-center shadow-[6px_6px_0px_0px_rgba(255,79,0,1)]" data-testid="final-cta">
          <h2 className="font-heading text-3xl md:text-4xl font-[400] tracking-tight mb-4">Ready to map your knowledge?</h2>
          <p className="font-body text-background/70 max-w-lg mx-auto mb-8">
            Join thousands of learners using Atlas to visualize and master complex subjects.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4">
            <Button size="lg" onClick={() => navigate('/register')} data-testid="final-cta-btn"
              className="bg-orange text-white hover:bg-orange/90 px-8 h-12 text-base font-body font-medium">
              Start Free <ArrowRight className="w-4 h-4 ml-2" strokeWidth={2} />
            </Button>
            <Button size="lg" variant="outline" onClick={() => token ? navigate('/canvas') : navigate('/login')}
              className="border-background/30 text-background hover:bg-background hover:text-foreground px-8 h-12 text-base font-body">
              Try Blank Canvas
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border py-8">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <Logo size="small" linkTo={null} />
            <div className="flex items-center gap-6">
              <Link to="/privacy" className="font-body text-sm text-muted-foreground hover:text-foreground transition-colors">Privacy</Link>
              <Link to="/terms" className="font-body text-sm text-muted-foreground hover:text-foreground transition-colors">Terms</Link>
            </div>
          </div>
          <div className="mt-6 pt-6 border-t border-border">
            <p className="font-mono text-[10px] text-muted-foreground/50 text-center">© 2026 Atlas. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
